import {
  CbPromoCodeKind,
  CbDiscountDuration,
  CreateCbPromoCodeInput,
  CbPromoCodeTargetInput,
} from 'api/graphql-global-types';
import { CbPromoCodeFormValues } from './cb-promo-code-form-values.interface';

const cleanTargets = (
  targets?: CbPromoCodeTargetInput[]
): CbPromoCodeTargetInput[] | undefined => {
  const filtered = (targets || []).filter((target) => Boolean(target?.product));

  return filtered.length
    ? filtered.map(({ product, recurrence }) => ({
        product,
        recurrence: recurrence || undefined,
      }))
    : undefined;
};

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

const requireFiniteNumber = (value: unknown, fieldLabel: string): number => {
  if (isBlank(value)) {
    throw new Error(`${fieldLabel} is required and must be a valid number`);
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error(`${fieldLabel} is required and must be a valid number`);
  }

  return parsed;
};

const optionalFiniteNumber = (value: unknown): number | undefined => {
  if (isBlank(value)) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Maps the create form values to CreateCbPromoCodeInput. Exactly one kind block
 * is attached, matching `kind` (the backend rejects a mismatch). Optional
 * windows/caps are omitted (never sent as null) so the backend defaults apply —
 * omitted totalUses = unlimited, omitted code = auto-generated.
 */
export const cbPromoCodeInputConverter = (
  values: CbPromoCodeFormValues
): CreateCbPromoCodeInput => {
  const input: CreateCbPromoCodeInput = {
    kind: values.kind,
    perAccountLimit: values.perAccountLimit,
    isActive: values.isActive,
  };

  const trimmedCode = values.code?.trim();
  if (trimmedCode) {
    input.code = trimmedCode;
  }

  if (values.startsAt) {
    input.startsAt = values.startsAt.toISOString();
  }

  if (values.expiresAt) {
    input.expiresAt = values.expiresAt.toISOString();
  }

  if (values.limitTotalUses && values.totalUses) {
    input.totalUses = values.totalUses;
  }

  const trimmedNote = values.note?.trim();
  if (trimmedNote) {
    input.note = trimmedNote;
  }

  switch (values.kind) {
    case CbPromoCodeKind.CreditFree:
      input.creditFree = {
        creditAmount: requireFiniteNumber(values.creditAmount, 'Credit amount'),
      };
      break;
    case CbPromoCodeKind.CreditMatch: {
      const maxPromoPerRedemption = values.capMaxPromoPerRedemption
        ? optionalFiniteNumber(values.maxPromoPerRedemption)
        : undefined;

      input.creditMatch = {
        matchPercent: requireFiniteNumber(values.matchPercent, 'Match percent'),
        ...(maxPromoPerRedemption != null ? { maxPromoPerRedemption } : {}),
      };
      break;
    }
    case CbPromoCodeKind.SubscriptionDiscount: {
      const durationMonths = optionalFiniteNumber(values.durationMonths);

      input.subscriptionDiscount = {
        percentOff: requireFiniteNumber(values.percentOff, 'Percent off'),
        duration: values.duration || CbDiscountDuration.once,
        ...(durationMonths != null ? { durationMonths } : {}),
        applicableTargets: cleanTargets(values.applicableTargets),
      };
      break;
    }
    case CbPromoCodeKind.SubscriptionTrial:
      input.subscriptionTrial = {
        trialDays: requireFiniteNumber(values.trialDays, 'Trial days'),
        applicableTargets: cleanTargets(values.applicableTargets),
      };
      break;
  }

  return input;
};
